import React from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { WatchProvider } from '@/contexts/WatchContext'
import { StartupWizardProvider } from '@/contexts/StartupWizardContext'
import Layout from './components/Layout/Layout'
import Login from './pages/Auth/Login'
import Signup from './pages/Auth/Signup'
import ForgotPassword from './pages/Auth/ForgotPassword'
import Dashboard from './pages/Dashboard/Dashboard'
import WatchedDashboard from './pages/Dashboard/WatchedDashboard'
import Screens from './pages/Screens/Screens'
import WatchedScreens from './pages/Screens/WatchedScreens'
import Devices from './pages/Devices/Devices'
import WatchedDevices from './pages/Devices/WatchedDevices'
import Schedule from './pages/Schedule/Schedule'
import WatchedSchedule from './pages/Schedule/WatchedSchedule'
import Channels from './pages/Channels/Channels'
import UserChannels from './pages/Channels/UserChannels'
import AIStudio from './pages/AIStudio/AIStudio'
import Analytics from './pages/Analytics/Analytics'
import ChannelStatistics from './pages/ChannelStatistics/ChannelStatistics'
import Subscriptions from './pages/Subscriptions/Subscriptions'
import Settings from './pages/Settings/Settings'
import Ads from './pages/Ads/Ads'
import Terms from './pages/Terms/Terms'
import Privacy from './pages/Privacy/Privacy'
import LoadingSpinner from './components/UI/LoadingSpinner'

import EmailVerification from './pages/Auth/EmailVerification'
import { hasAdminPrivileges, isDemoUser, isTestUser } from '@/utils/adminUtils'
import UserService from './utils/services/UserService';
import ConfigService from './utils/services/ConfigService';

import UserServiceTest from './components/UserServiceTest'

import GlobalDeviceMonitor from './components/GlobalDeviceMonitor'
import StartupWizard from './components/StartupWizard/StartupWizard'

// Custom redirect component for /code route
const CodeRedirect = () => {
  const navigate = useNavigate()
  
  React.useEffect(() => {
    navigate('/login?redirect=devices&openAddDevice=true', { replace: true })
  }, [navigate])
  
  return <LoadingSpinner size="lg" />
}

// Custom redirect component for authenticated users visiting /code
const AuthenticatedCodeRedirect = () => {
  const navigate = useNavigate()
  
  React.useEffect(() => {
    navigate('/devices?openAddDevice=true', { replace: true })
  }, [navigate])
  
  return <LoadingSpinner size="lg" />
}

// Admin route component - prevents non-admin users from accessing certain pages
const AdminRoute = ({ children }) => {
  const { currentUserData } = useAuth()
  
  if (!hasAdminPrivileges(currentUserData)) {
    return <Navigate to="/dashboard" replace />
  }
  
  return children
}

// Demo user route component - prevents demo users from accessing certain pages
const DemoUserRoute = ({ children }) => {
  const { currentUserData } = useAuth()
  
  if (isDemoUser(currentUserData)) {
    return <Navigate to="/dashboard" replace />
  }
  
  return children
}

// Test user route component - prevents test users from accessing certain pages
const TestUserRoute = ({ children }) => {
  const { currentUserData } = useAuth()
  
  if (isTestUser(currentUserData)) {
    return <Navigate to="/dashboard" replace />
  }
  
  return children
}



function App() {
  const { currentUser, currentUserData, loading } = useAuth()
  const [configLoading, setConfigLoading] = React.useState(true)

  React.useEffect(() => {
    // Modern Firebase is initialized in @/config/firebase
    
    // Preload ConfigService configuration
    const preloadConfig = async () => {
      try {
        setConfigLoading(true)
        await ConfigService.preloadConfig()
      } catch (error) {
        console.error('Failed to preload ConfigService:', error)
        // Continue with app initialization even if config fails to load
      } finally {
        setConfigLoading(false)
      }
    }
    
    preloadConfig()
  }, []);

  if (loading || configLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!currentUser) {
    return (
      <>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/code" element={<CodeRedirect />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </>
    )
  }

  // Check if user email is verified (except for admin users and test users)
  if (!currentUser.emailVerified && !UserService.isUserAdmin(currentUserData) && !UserService.isUserTest(currentUserData)) {
    return (
      <>
        <EmailVerification />
      </>
    )
  }

  return (
    <>
      <StartupWizardProvider>
        <WatchProvider>
          <GlobalDeviceMonitor />
          <StartupWizard />
          <Layout>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<WatchedDashboard />} />
              <Route path="/screens" element={<WatchedScreens />} />
              <Route path="/devices" element={
                (() => {
                  console.log('🔍 App.jsx - /devices route matched, rendering WatchedDevices')
                  return <WatchedDevices />
                })()
              } />
              <Route path="/test-route" element={<div>Test Route Working!</div>} />
              <Route path="/schedule" element={<WatchedSchedule />} />
              <Route path="/user-channels" element={<UserChannels />} />
              <Route path="/code" element={<AuthenticatedCodeRedirect />} />
              <Route path="/analytics" element={
                <TestUserRoute>
                  <Analytics />
                </TestUserRoute>
              } />
              <Route path="/ai-studio" element={
                <AdminRoute>
                  <AIStudio />
                </AdminRoute>
              } />
              <Route path="/channels" element={
                <AdminRoute>
                  <Channels />
                </AdminRoute>
              } />
              <Route path="/channel-statistics" element={
                <AdminRoute>
                  <ChannelStatistics />
                </AdminRoute>
              } />
              <Route path="/ads" element={
                <AdminRoute>
                  <Ads />
                </AdminRoute>
              } />
              <Route path="/subscriptions" element={
                <TestUserRoute>
                  <DemoUserRoute>
                    <Subscriptions />
                  </DemoUserRoute>
                </TestUserRoute>
              } />
              <Route path="/settings" element={<Settings />} />
      
              <Route path="/test-user-service" element={
                <AdminRoute>
                  <UserServiceTest />
                </AdminRoute>
              } />
              
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Layout>
        </WatchProvider>
      </StartupWizardProvider>
    </>
  )
}

export default App 